/** PaySafeGo form-builder model. Shape follows docs/dynamicForms; this file is the product copy. */

export const FIELD_TYPES = [
  { type: 'text', name: 'Short text', icon: 'Aa' },
  { type: 'longtext', name: 'Long text', icon: '¶' },
  { type: 'number', name: 'Number', icon: '#' },
  { type: 'email', name: 'Email', icon: '@' },
  { type: 'url', name: 'Link (URL)', icon: '↗' },
  { type: 'date', name: 'Date', icon: '▦' },
  { type: 'file', name: 'File upload', icon: '⤒' },
  { type: 'dropdown', name: 'Dropdown', icon: '▾' },
  { type: 'radio', name: 'Radio', icon: '◉' },
  { type: 'checkboxes', name: 'Checkboxes', icon: '☑' },
  { type: 'yesno', name: 'Yes / No', icon: '⇄' },
  { type: 'matrix', name: 'Matrix / grid', icon: '▦▦' },
  { type: 'table', name: 'Table (rows)', icon: '☰+' },
]

export const CHOICE_TYPES = ['dropdown', 'radio', 'checkboxes', 'yesno']
export const ALWAYS_FULL = ['longtext', 'matrix', 'table', 'file']
export const NO_CONDITION_SOURCE = ['matrix', 'table', 'file']

export const OPERATORS = [
  { v: 'equals', label: 'equals' },
  { v: 'not_equals', label: 'does not equal' },
  { v: 'greater_than', label: 'is greater than' },
  { v: 'gte', label: 'is at least (≥)' },
  { v: 'less_than', label: 'is less than' },
  { v: 'lte', label: 'is at most (≤)' },
  { v: 'is_empty', label: 'is empty' },
  { v: 'is_not_empty', label: 'is not empty' },
]

const NUMERIC_OPS = ['greater_than', 'gte', 'less_than', 'lte']
const VALUELESS_OPS = ['is_empty', 'is_not_empty']

export const STATIC_CATALOG = { value: '', label: 'Static options' }

export const LIVE_CATALOGS = [
  { value: 'venues', label: 'Venues' },
  { value: 'staff', label: 'Staff names' },
  { value: 'users', label: 'Application users' },
  { value: 'roles', label: 'Roles' },
  { value: 'countries', label: 'Countries' },
  { value: 'au_cities', label: 'Australian cities' },
]

/** @deprecated use catalogsForFacing */
export const CATALOGS = [STATIC_CATALOG, ...LIVE_CATALOGS]

export function catalogsForFacing(facing) {
  if (facing === 'patron') return [STATIC_CATALOG]
  return [STATIC_CATALOG, ...LIVE_CATALOGS]
}

export function collectCatalogKeys(definition) {
  const keys = new Set()
  for (const field of flattenFields(definition?.pages)) {
    if (field.catalog) keys.add(field.catalog)
  }
  return [...keys]
}

export function mergeCatalogOptions(definition, catalogCache) {
  return {
    ...definition,
    pages: (definition?.pages || []).map((p) => ({
      ...p,
      sections: (p.sections || []).map((s) => ({
        ...s,
        fields: (s.fields || []).map((f) =>
          f.catalog && catalogCache?.[f.catalog] ? { ...f, options: catalogCache[f.catalog] } : f
        ),
      })),
    })),
  }
}

export function stripCatalogFields(definition) {
  return {
    ...definition,
    pages: (definition?.pages || []).map((p) => ({
      ...p,
      sections: (p.sections || []).map((s) => ({
        ...s,
        fields: (s.fields || []).map((f) => {
          if (!f.catalog) return f
          const { catalog, ...rest } = f
          return rest
        }),
      })),
    })),
  }
}

export const DEFAULT_LOCALE = 'en'

export const LOCALES = [
  { code: 'en', label: 'English', englishName: 'English', nativeName: 'English' },
  { code: 'zh', label: 'Chinese (Simplified) · 中文 (简体)', englishName: 'Chinese (Simplified)', nativeName: '中文 (简体)' },
  { code: 'zh-Hant', label: 'Chinese (Traditional) · 中文 (繁體)', englishName: 'Chinese (Traditional)', nativeName: '中文 (繁體)' },
  { code: 'vi', label: 'Vietnamese · Tiếng Việt', englishName: 'Vietnamese', nativeName: 'Tiếng Việt' },
  { code: 'ar', label: 'Arabic · العربية', englishName: 'Arabic', nativeName: 'العربية' },
  { code: 'ko', label: 'Korean · 한국어', englishName: 'Korean', nativeName: '한국어' },
]

export const PLACEMENT_SLOTS = [
  { value: 'ecdd.sof', label: 'ECDD — Source of funds / wealth' },
  { value: 'ecdd.extra', label: 'ECDD — Additional forms' },
  { value: 'profile.extra', label: 'Profile — Additional forms' },
  { value: 'investigation.extra', label: 'Investigation — Additional forms' },
]

export function localeName(code, { style = 'both' } = {}) {
  const row = LOCALES.find((l) => l.code === code)
  if (!row) return code
  if (style === 'english') return row.englishName
  if (style === 'native') return row.nativeName
  return row.label
}

export function normalizeLocales(settings) {
  const list = Array.isArray(settings?.locales) ? settings.locales.map(String).filter(Boolean) : []
  return list.includes(DEFAULT_LOCALE) ? list : [DEFAULT_LOCALE, ...list]
}

export function normalizeLocaleMeta(settings) {
  return settings?.localeMeta && typeof settings.localeMeta === 'object' ? settings.localeMeta : {}
}

export function getLocaleMeta(settings, code) {
  const meta = normalizeLocaleMeta(settings)[code] || {}
  return {
    mode: meta.mode === 'auto' ? 'auto' : 'manual',
    reviewed: !!meta.reviewed,
    autoGeneratedAt: meta.autoGeneratedAt || null,
    provider: meta.provider || null,
  }
}

export function patchLocaleMetaSettings(settings, code, patch) {
  const localeMeta = { ...normalizeLocaleMeta(settings), [code]: { ...getLocaleMeta(settings, code), ...patch } }
  return { ...settings, localeMeta }
}

export function normalizePlacements(settings) {
  return Array.isArray(settings?.placements) ? settings.placements.map(String).filter(Boolean) : []
}

let idn = 1
export const uid = (p) => `${p}${Date.now().toString(36)}${idn++}`

export function emptyDefinition() {
  return {
    schemaVersion: 1,
    pages: [{ id: uid('p'), title: 'Page 1', sections: [{ id: uid('s'), label: null, columns: 1, fields: [] }] }],
  }
}

export const FORM_WIDTHS = [
  { v: 'narrow', label: 'Narrow', hint: '680px, centered — best for readability.' },
  { v: 'wide', label: 'Wide', hint: '920px, centered — suits 3-column sections.' },
  { v: 'full', label: 'Full', hint: 'Fills the window. Long text lines get hard to read — use with multi-column sections.' },
  { v: 'custom', label: 'Custom', hint: 'Centered at the size you set. % is relative to the window.' },
]

export const BG_SWATCHES = ['#F2F4F3', '#FFFFFF', '#EAF1F7', '#F7F1E8', '#1B2321']

export const TIMEZONES = [
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Brisbane',
  'Australia/Adelaide',
  'Australia/Perth',
  'Australia/Darwin',
  'Pacific/Auckland',
  'UTC',
]

const DEFAULT_CONFIRM = 'Thank you — your submission has been received.'

const DEFAULT_SUBMIT = 'Submit'
const DEFAULT_SUBMITTING = 'Submitting…'

export function emptySettings() {
  return {
    schemaVersion: 1,
    locales: [DEFAULT_LOCALE],
    placements: [],
    displayTitle: null,
    submitLabel: DEFAULT_SUBMIT,
    submittingLabel: DEFAULT_SUBMITTING,
    confirmMessage: DEFAULT_CONFIRM,
    appearance: { bgColor: '#F2F4F3', bgImage: '', width: 'narrow', customValue: 800, customUnit: 'px' },
    afterSubmission: {
      action: 'message',
      confirmMessage: DEFAULT_CONFIRM,
      allowResubmit: false,
      redirectUrl: '',
      appendRef: true,
    },
    availability: { opensAt: '', closesAt: '', maxSubmissions: '', closedMessage: '' },
    timezone: 'Australia/Sydney',
    audience: { access: 'invite', captcha: false, oneResponsePer: 'invite' },
    drafts: { onNewVersion: 'pin', retentionDays: 30 },
    workflow: { enabled: false, steps: [] },
    recordCode: '',
  }
}

/** Short code for Form records navigation — set in form settings or derived from slug. */
export function formRecordCode(form) {
  const raw = String(form?.workingSettings?.recordCode || form?.settings?.recordCode || '').trim()
  if (raw) return raw.toUpperCase()
  const slug = String(form?.slug || '').trim()
  if (!slug) return '—'
  const parts = slug.split('-').filter(Boolean)
  if (parts.length >= 2) {
    return parts.slice(0, 2).map((p) => p.slice(0, 4).toUpperCase()).join('-')
  }
  return slug.slice(0, 8).toUpperCase()
}

export function normalizeAppearance(settings) {
  const a = settings?.appearance && typeof settings.appearance === 'object' ? settings.appearance : {}
  const width = a.width || settings?.width
  return {
    bgColor: a.bgColor || settings?.bgColor || '#F2F4F3',
    bgImage: a.bgImage || settings?.bgImage || '',
    width: ['narrow', 'wide', 'full', 'custom'].includes(width) ? width : 'narrow',
    customValue: a.customValue ?? settings?.customValue ?? 800,
    customUnit: a.customUnit === '%' || settings?.customUnit === '%' ? '%' : 'px',
    showTenantLogo: !!(a.showTenantLogo || a.showClubLogo),
    useClubColors: !!a.useClubColors,
  }
}

function parseHexColor(hex) {
  const s = String(hex || '').trim()
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(s)
  if (!m) return null
  return { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) }
}

/** Light page background from a brand hex (mostly white). */
export function brandTintBackground(hex, mix = 0.92) {
  const rgb = parseHexColor(hex)
  if (!rgb) return null
  const w = Math.min(1, Math.max(0, Number(mix) || 0.92))
  const r = Math.round(rgb.r * (1 - w) + 255 * w)
  const g = Math.round(rgb.g * (1 - w) + 255 * w)
  const b = Math.round(rgb.b * (1 - w) + 255 * w)
  return `rgb(${r}, ${g}, ${b})`
}

export function formAccentColor(settings, branding) {
  const a = normalizeAppearance(settings)
  if (a.useClubColors) {
    const c = branding?.primaryColor || branding?.highlightColor
    if (c && parseHexColor(c)) return c.startsWith('#') ? c : `#${c}`
  }
  return '#0f766e'
}

export function shouldShowTenantLogo(settings) {
  return normalizeAppearance(settings).showTenantLogo
}

export function shouldUseClubColors(settings) {
  return normalizeAppearance(settings).useClubColors
}

export function normalizeAfterSubmission(settings) {
  const a = settings?.afterSubmission && typeof settings.afterSubmission === 'object' ? settings.afterSubmission : {}
  const action = a.action || settings?.afterAction
  return {
    action: action === 'redirect' ? 'redirect' : 'message',
    confirmMessage: a.confirmMessage ?? settings?.confirmMessage ?? DEFAULT_CONFIRM,
    allowResubmit: !!(a.allowResubmit ?? settings?.allowResubmit),
    redirectUrl: a.redirectUrl || settings?.redirectUrl || '',
    appendRef: a.appendRef !== false && settings?.appendRef !== false,
  }
}

export function normalizeAvailability(settings) {
  const a = settings?.availability && typeof settings.availability === 'object' ? settings.availability : {}
  return {
    opensAt: a.opensAt || a.openAt || '',
    closesAt: a.closesAt || a.closeAt || '',
    maxSubmissions: a.maxSubmissions ?? a.quota ?? '',
    closedMessage: a.closedMessage || '',
  }
}

export function normalizeDrafts(settings) {
  const d = settings?.drafts && typeof settings.drafts === 'object' ? settings.drafts : {}
  const days = Number(d.retentionDays)
  return {
    onNewVersion: d.onNewVersion === 'invalidate' ? 'invalidate' : 'pin',
    retentionDays: Number.isFinite(days) && days > 0 ? days : 30,
  }
}

export function formPageStyle(settings, branding) {
  const a = normalizeAppearance(settings)
  const style = {}
  const defaultBg = '#F2F4F3'
  const manualBg = a.bgColor || defaultBg
  const manualOverride =
    manualBg.toLowerCase() !== defaultBg.toLowerCase() || (a.bgImage && String(a.bgImage).trim() !== '')
  if (a.useClubColors && branding?.primaryColor && !manualOverride) {
    const tinted = brandTintBackground(branding.primaryColor)
    if (tinted) style.backgroundColor = tinted
  }
  if (!style.backgroundColor) {
    style.backgroundColor = manualBg
  }
  if (a.bgImage) {
    style.backgroundImage = `url(${a.bgImage})`
    style.backgroundSize = 'cover'
    style.backgroundPosition = 'center'
  }
  return style
}

function parseCssRgb(color) {
  const hex = parseHexColor(color)
  if (hex) return hex
  const m = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(String(color || '').trim())
  if (!m) return null
  return { r: Number(m[1]), g: Number(m[2]), b: Number(m[3]) }
}

/** True when page fill is dark enough that outside-chrome text should be light. */
export function formPageBackgroundIsDark(settings, branding) {
  const bg = formPageStyle(settings, branding).backgroundColor
  const rgb = parseCssRgb(bg)
  if (!rgb) return false
  const lin = [rgb.r, rgb.g, rgb.b].map((v) => {
    const c = v / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  })
  const luminance = 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2]
  return luminance < 0.45
}

export function formCardMaxWidth(settings) {
  const a = normalizeAppearance(settings)
  if (a.width === 'wide') return 920
  if (a.width === 'full') return '100%'
  if (a.width === 'custom') {
    const n = Number(a.customValue)
    const value = Number.isFinite(n) && n > 0 ? n : 800
    return a.customUnit === '%' ? `${value}%` : value
  }
  return 680
}

export function isSafeRedirectUrl(url) {
  try {
    const parsed = new URL(String(url || ''))
    return parsed.protocol === 'https:'
  } catch {
    return false
  }
}

export function resolveFormDisplayTitle(settings, formName, locale = DEFAULT_LOCALE) {
  const fromSettings = labelOf(settings?.displayTitle, locale, { fallback: false })
  if (String(fromSettings || '').trim()) return fromSettings
  return formName || 'Form'
}

export function resolveFormSubmitLabel(settings, locale = DEFAULT_LOCALE, { busy = false } = {}) {
  if (busy) {
    const submitting = labelOf(settings?.submittingLabel, locale)
    if (String(submitting || '').trim()) return submitting
    return DEFAULT_SUBMITTING
  }
  const submit = labelOf(settings?.submitLabel, locale)
  if (String(submit || '').trim()) return submit
  return DEFAULT_SUBMIT
}

export function displayTitleSource(settings, formName) {
  const en = labelOf(settings?.displayTitle, DEFAULT_LOCALE, { fallback: false })
  if (String(en || '').trim()) return settings?.displayTitle ?? en
  return formName || ''
}

function slugifyLabel(value) {
  return String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')
}

export function resolveConfirmMessage(settings, { locale = DEFAULT_LOCALE, answers = {}, definition, formName = '', ref = '' } = {}) {
  const after = normalizeAfterSubmission(settings)
  let text = labelOf(after.confirmMessage, locale) || DEFAULT_CONFIRM
  const fields = flattenFields(definition?.pages || [])
  const year = String(new Date().getFullYear())
  return text.replace(/\{\{\s*([^}]+)\s*\}\}/gi, (_, raw) => {
    const key = String(raw).split('|')[0].trim().toLowerCase()
    if (key === 'current_year') return year
    if (key === 'submission_id' || key === 'submission_ref') return ref || ''
    if (key === 'form_name') return formName || ''
    if (key.startsWith('field.')) {
      const slug = key.slice(6)
      const field = fields.find((f) => f.id.toLowerCase() === slug || slugifyLabel(labelOf(f.label, locale)) === slug)
      if (!field) return ''
      const v = answers?.[field.id]
      if (v == null || v === '') return ''
      return Array.isArray(v) ? v.join(', ') : String(v)
    }
    return ''
  })
}

export function afterSubmitRedirect(settings, ref) {
  const after = normalizeAfterSubmission(settings)
  if (after.action !== 'redirect' || !isSafeRedirectUrl(after.redirectUrl)) return null
  const url = new URL(after.redirectUrl)
  if (after.appendRef && ref) url.searchParams.set('ref', ref)
  return url.toString()
}

export const WORKFLOW_ASSIGNEE_TYPES = [
  { value: 'initiator', label: 'The initiator (submitter)' },
  { value: 'user', label: 'An application user' },
  { value: 'role', label: 'A role (anyone can claim)' },
]

export function normalizeAssignee(assignee) {
  const raw = assignee && typeof assignee === 'object' ? assignee : { type: 'initiator' }
  let type = raw.type || 'initiator'
  if (type === 'person') type = 'user'
  if (type === 'group') type = 'role'
  if (!['initiator', 'user', 'role'].includes(type)) type = 'initiator'
  return {
    type,
    id: raw.id || raw.value || '',
    label: raw.label || '',
  }
}

export function normalizeWorkflowStep(step) {
  if (!step || typeof step !== 'object') return null
  const type = step.type === 'fill' || step.type === 'approve' ? step.type : 'approve'
  return {
    id: step.id || uid('w'),
    type,
    name: step.name || step.label || (type === 'fill' ? 'Fill form' : 'Approval'),
    assignee: normalizeAssignee(step.assignee),
    sections: type === 'fill' ? (Array.isArray(step.sections) ? step.sections : []) : undefined,
    condition: step.condition,
  }
}

export function defaultWorkflowSteps(pages) {
  const sectionIds = (pages || []).flatMap((p) => (p.sections || []).map((s) => s.id))
  return [
    {
      id: uid('w'),
      type: 'fill',
      name: 'Fill form',
      assignee: { type: 'initiator', id: '', label: '' },
      sections: sectionIds,
    },
    {
      id: uid('w'),
      type: 'approve',
      name: 'Approval',
      assignee: { type: 'role', id: '', label: '' },
    },
  ]
}

export function assigneeDisplay(assignee) {
  const a = normalizeAssignee(assignee)
  if (a.type === 'initiator') return 'Initiator (submitter)'
  if (a.type === 'user') return a.label || 'Named user'
  return a.label ? `${a.label} (role)` : 'Role (anyone can claim)'
}

export function labelOf(v, locale = DEFAULT_LOCALE, { fallback = true } = {}) {
  if (v == null) return ''
  if (typeof v === 'string') {
    if (locale === DEFAULT_LOCALE) return v
    return fallback ? v : ''
  }
  if (typeof v !== 'object') return String(v)
  const direct = v[locale]
  if (String(direct ?? '').trim() !== '') return direct
  if (!fallback) return ''
  return v.en || Object.values(v).find(Boolean) || ''
}

/** Value shown in translation inputs — never falls back to English. */
export function localizedEditValue(v, locale = DEFAULT_LOCALE) {
  return labelOf(v, locale, { fallback: false })
}

/** Section titles may be null when the default locale is blank; other locales must not wipe English. */
export function nullableLocalizedLabel(value, locale = DEFAULT_LOCALE) {
  if (locale === DEFAULT_LOCALE) {
    return labelOf(value, DEFAULT_LOCALE) ? value : null
  }
  return value
}

/** Store a translatable string. English-only stays a plain string until another locale is set. */
export function setLocalized(current, locale, value) {
  const loc = locale || DEFAULT_LOCALE
  const text = value == null ? '' : String(value)
  if (loc === DEFAULT_LOCALE && (current == null || typeof current === 'string')) return text
  const base = typeof current === 'object' && current
    ? { ...current }
    : { en: typeof current === 'string' ? current : '' }
  base[loc] = text
  return base
}

export function optionLine(o, locale = DEFAULT_LOCALE, { fallback = true } = {}) {
  if (o == null) return ''
  if (typeof o === 'string') {
    if (locale === DEFAULT_LOCALE) return o
    return fallback ? o : ''
  }
  const text = labelOf(o.label, locale, { fallback })
  if (String(text).trim() !== '') return text
  return fallback ? (o.value || '') : ''
}

export function optionValue(o) {
  if (o == null) return ''
  if (typeof o === 'string') return o
  return o.value != null ? String(o.value) : labelOf(o.label) || ''
}

export function optionIsBlank(o) {
  if (o == null) return true
  if (typeof o === 'string') return !o.trim()
  const v = o.value != null ? String(o.value).trim() : ''
  const label = String(labelOf(o.label) || '').trim()
  return !v && !label
}

export function parseOptionLines(text, previous, locale = DEFAULT_LOCALE) {
  // Keep a trailing blank line so Enter can start the next option.
  // filter(Boolean) was dropping that newline immediately and made Enter look broken.
  const lines = String(text ?? '').split('\n').map((s) => s.trim())
  const prev = Array.isArray(previous) ? previous : []
  return lines.map((line, i) => {
    const old = prev[i]
    if (locale === DEFAULT_LOCALE && (old == null || typeof old === 'string')) return line
    const value = typeof old === 'string' ? old : (old?.value || line)
    const label = setLocalized(typeof old === 'object' ? old.label : old, locale, line)
    return { value, label }
  })
}

export function typeName(type) {
  return FIELD_TYPES.find((t) => t.type === type)?.name || type
}

export function flattenFields(pages) {
  const out = []
  for (const page of pages || []) {
    for (const section of page.sections || []) {
      for (const field of section.fields || []) out.push(field)
    }
  }
  return out
}

export function fieldSectionMap(definition) {
  const map = new Map()
  for (const page of definition?.pages || []) {
    for (const section of page.sections || []) {
      for (const field of section.fields || []) map.set(field.id, section)
    }
  }
  return map
}

export function isReviewerOnlySection(section) {
  return section?.reviewerOnly === true
}

export function isReviewerOnlyField(field, section) {
  return field?.reviewerOnly === true || isReviewerOnlySection(section)
}

export function hasReviewerOnlyContent(definition) {
  for (const page of definition?.pages || []) {
    for (const section of page.sections || []) {
      if (isReviewerOnlySection(section)) return true
      for (const field of section.fields || []) {
        if (field.reviewerOnly) return true
      }
    }
  }
  return false
}

export function opsForSource(srcField) {
  if (srcField && ['number', 'date'].includes(srcField.type)) return OPERATORS
  return OPERATORS.filter((o) => !NUMERIC_OPS.includes(o.v))
}

export function operatorNeedsValue(op) {
  return !VALUELESS_OPS.includes(op)
}

export function condChipText(cond, pages) {
  if (!cond?.fieldId) return null
  const src = flattenFields(pages).find((f) => f.id === cond.fieldId)
  if (!src) return null
  const op = OPERATORS.find((o) => o.v === cond.operator)?.label || cond.operator
  const val = operatorNeedsValue(cond.operator) ? ` “${cond.value ?? ''}”` : ''
  return `If ${labelOf(src.label) || src.id} ${op}${val}`
}

export function defaultField(type) {
  const def = FIELD_TYPES.find((t) => t.type === type)
  const f = { id: uid('f'), type, label: `New ${(def?.name || type).toLowerCase()}`, required: false }
  if (type === 'yesno') f.options = ['Yes', 'No']
  else if (CHOICE_TYPES.includes(type)) f.options = ['Option 1', 'Option 2']
  if (type === 'matrix') {
    f.rows = ['Row 1', 'Row 2']
    f.options = ['1', '2', '3']
  }
  if (type === 'table') f.columns2 = [{ id: uid('c'), label: 'Column 1', type: 'text' }]
  if (ALWAYS_FULL.includes(type)) f.span = 'full'
  return f
}

export function pageTitle(page, index, locale = DEFAULT_LOCALE) {
  return labelOf(page?.title, locale) || `Page ${index + 1}`
}
